import { Vector2 } from 'three'
import type { IViewSize } from '@/plugins/cad/module/common/utils'
import type { EngineContext } from '@/runtime/EngineContext'
import type { ViewRegistry } from '@/shared/types/services'
import { screenToViewNDC } from '@/shared/utils/viewRect'

type ViewRect = {
  x: number
  y: number
  width: number
  height: number
}

const toViewportSize = (rect: ViewRect): IViewSize => {
  return {
    clientWidth: Math.max(1, Math.round(rect.width)),
    clientHeight: Math.max(1, Math.round(rect.height)),
  }
}

/**
 * 获取当前激活视图。
 *
 * @param ctx - 当前引擎上下文
 * @returns 激活视图对象；未注册 ViewRegistry 服务或无激活视图时返回 undefined
 */
export const getActiveView = (ctx: EngineContext): ReturnType<ViewRegistry['getActive']> => {
  if (!ctx.hasService('ViewRegistry')) {
    return undefined
  }

  return ctx.getService('ViewRegistry').getActive()
}

/**
 * 获取当前激活视图的视口矩形。
 *
 * @param ctx - 当前引擎上下文
 * @returns 激活视图的矩形；不可用时回退到渲染器画布尺寸
 */
export const getActiveViewRect = (ctx: EngineContext): ViewRect => {
  const activeRect = getActiveView(ctx)?.rect
  if (activeRect) {
    return activeRect
  }

  // 回退尺寸取自 backend 的 surface（canvas clientRect / logicalSize），不读 legacy renderer：
  // WebGPU 下 `ctx.renderer` 会抛后端不匹配。缺少 graphics 的旧上下文（测试桩）退化为 1x1。
  const graphics = (ctx as { graphics?: EngineContext['graphics'] }).graphics
  const canvas = graphics?.canvas
  const surface = graphics?.surface
  const width = canvas?.clientWidth || surface?.clientRect.width || surface?.logicalWidth || 1
  const height = canvas?.clientHeight || surface?.clientRect.height || surface?.logicalHeight || 1

  return {
    x: 0,
    y: 0,
    width: Math.max(1, Math.round(width)),
    height: Math.max(1, Math.round(height)),
  }
}

/**
 * 获取当前激活视图的像素尺寸。
 *
 * @param ctx - 当前引擎上下文
 * @returns 视口宽高，最小值为 1 像素
 */
export const getActiveViewSize = (ctx: EngineContext): IViewSize => {
  return toViewportSize(getActiveViewRect(ctx))
}

/**
 * 将屏幕坐标转换为当前激活视图的 NDC 坐标。
 *
 * @param ctx - 当前引擎上下文
 * @param screenPos - 屏幕坐标，单位像素
 * @returns NDC 坐标，取值范围 [-1, 1]
 */
export const screenToActiveViewNDC = (
  ctx: EngineContext,
  screenPos: { x: number; y: number }
): Vector2 => {
  return screenToViewNDC(new Vector2(screenPos.x, screenPos.y), getActiveViewRect(ctx))
}
